import React from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import { Box, Flex, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { PlaylistIcon } from '../assets/icons/PlaylistIcon';

const messages = defineMessages({
	playlistHeader: {
		id: 'ai-audio.playlist.header',
		defaultMessage: 'Playlist',
		description: 'Header for the playlist',
	},
	collapsePlaylistLabel: {
		id: 'ai-audio.playlist.header.collapse-label',
		defaultMessage: 'Collapse playlist',
		description: 'Accessible label for collapsing the playlist',
	},
	expandPlaylistLabel: {
		id: 'ai-audio.playlist.header.expand-label',
		defaultMessage: 'Expand playlist',
		description: 'Accessible label for expanding the playlist',
	},
} as const);

interface PlaylistHeaderProps {
	isExpanded: boolean;
	onToggle: () => void;
}

const styles = cssMap({
	container: {
		paddingTop: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
		paddingRight: token('space.050'),
		position: 'sticky',
		top: 0,
		zIndex: 1,
		height: `40px`, //safari sticky elements need a fixed height in order not to flicker
		backgroundColor: token('elevation.surface'),
		transform: 'translate3d(0, 0, 0)', //safari Flicker fix hack
		// @ts-ignore
		'-webkit-backface-visibility': 'hidden', //safari Flicker fix hack
	},
	containerExpanded: {
		paddingBottom: token('space.075'),
		paddingLeft: token('space.075'),
		paddingRight: token('space.075'),
		paddingTop: token('space.075'),
	},
	headerButton: {
		paddingTop: token('space.075'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		cursor: 'pointer',
		width: '100%',
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.medium'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	headerButtonExpanded: {
		paddingBottom: token('space.100'),
		paddingTop: token('space.100'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
	},
	minifiedAudioPlayerContainer: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
	},
});

export const PlaylistHeader: React.FC<PlaylistHeaderProps> = ({ isExpanded, onToggle }) => {
	const { formatMessage } = useIntl();

	return (
		<>
			<Box xcss={cx(styles.container, isExpanded && styles.containerExpanded)}>
				<Pressable
					onClick={onToggle}
					xcss={cx(styles.headerButton, isExpanded && styles.headerButtonExpanded)}
					aria-label={formatMessage(
						isExpanded ? messages.collapsePlaylistLabel : messages.expandPlaylistLabel,
					)}
				>
					<Flex justifyContent="space-between" alignItems="center">
						<Flex gap="space.100" alignItems="center">
							<PlaylistIcon />
							<Heading as="h2" size="xsmall">
								{formatMessage(messages.playlistHeader)}
							</Heading>
						</Flex>
						{isExpanded ? (
							<ChevronDownIcon label="" size="medium" LEGACY_margin="-4px" />
						) : (
							<ChevronUpIcon label="" size="medium" LEGACY_margin="-4px" />
						)}
					</Flex>
				</Pressable>
			</Box>
		</>
	);
};
